import React, { useState } from 'react';
import {
  Box,
  makeStyles,
  Button,
  Checkbox,
  InputBase,
  RadioGroup,
  Radio,
  FormControlLabel,
  withStyles,
  Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useForm, Controller } from 'react-hook-form';
import Joi from '@hapi/joi';
import { joiResolver } from '@hookform/resolvers/joi';
import config from '@/configs/config';
import formErrorMessage from '@/configs/formErrorMessage';
import { ErrorMessage } from '@hookform/error-message';
import moment from 'moment';
import { GENDER, GENDER_STRING_MAP } from '../const';
import ProgressBackdrop from '../../preorder/components/ProgressBackdrop';
import ProfileBanner from '../../ooha202304/components/ProfileBanner';
import EnterpriseCardPayVerifyModal from './EnterpriseCardPayVerifyModal';

const StyledRadio = withStyles(() => ({
  root: {
    '&$checked': {
      color: '#000000',
    },
  },
  checked: {},
}))(Radio);

const StyledButton = withStyles({
  root: {
    color: 'white',
    fontSize: 14,
    fontWeight: 'bold',
    borderRadius: 8,
    height: 28,
    backgroundColor: 'rgb(45,152,37)',
    '&:hover': {
      backgroundColor: 'rgb(45,152,37) !important',
      '&.Mui-disabled': {
        color: 'white',
        backgroundColor: 'rgb(216,216,216) !important',
      },
    },
    '&.Mui-disabled': {
      color: 'white',
      backgroundColor: 'rgb(216,216,216)',
    },
  },
  contained: {
    boxShadow: 'none',
  },
})(Button);

const useSectionStyle = makeStyles((theme) => ({
  root: {
    '&+&': {
      marginTop: theme.spacing(5),
    },
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      flexBasis: '48%',
      '&+&': {
        marginTop: 0,
      },
    },
  },
  title: {
    marginLeft: theme.spacing(3.5),
    fontWeight: 'bold',
    fontSize: 18,
    flexShrink: 0,
  },
  fieldsBox: {
    fontSize: 14,
    flexGrow: 1,
    boxShadow: '0px 0px 8px 0px rgba(0,0,0,0.1)',
    marginTop: theme.spacing(2),
    padding: theme.spacing(4, 3.5),
  },
}));

const useLabelFieldStyle = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  fieldContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',

    [theme.breakpoints.up('sm')]: {
      flexWrap: 'nowrap',
    },
  },
  fieldVertical: {
    flexDirection: 'column',
    alignItems: 'start',
  },
  label: {
    flexBasis: '100% !important',
    flexShrink: 0,
    fontSize: 14,
    marginBottom: theme.spacing(1),

    [theme.breakpoints.up('sm')]: {
      flexBasis: 'auto !important',
      marginBottom: 0,
    },
  },
  input: {
    background: '#ffffff',
    border: '1px solid rgb(235, 235, 235)',
    borderRadius: 8,
    flexBasis: '28%',
    flexGrow: 1,
    fontSize: 12,
    height: 36,
  },
  error: {
    fontSize: 14,
    color: 'red',
    margin: 0,
  },
  errorContainer: {
    minHeight: 24,
    overflow: 'hidden',
    paddingLeft: '0 !important',

    [theme.breakpoints.up('sm')]: {
    },
  },
  readOnlyField: {
    lineHeight: '36px',
  },
}));

const Section = ({ children, title }) => {
  const classes = useSectionStyle();

  return (
    <Box className={classes.root}>
      <Box className={classes.title}>{title}</Box>
      <Box className={classes.fieldsBox}>
        {children}
      </Box>
    </Box>
  );
};

export const LabelTextField = ({
  disabled,
  errors,
  isRequired,
  label,
  labelWidth,
  name,
  placeholder,
  readOnly,
  ref,
  toUpperCase,
  value,
  ...restProps
}) => {
  const classes = useLabelFieldStyle();

  return (
    <div className={classes.container}>
      <div className={classes.fieldContainer}>
        <div className={classes.label} style={{ width: labelWidth || 100 }}>
          {label}
          {isRequired && <span style={{ color: 'red' }}>*</span>}
        </div>
        {readOnly
          ? (
            <span className={classes.readOnlyField}>{value}</span>
          )
          : (
            <InputBase
              className={classes.input}
              disabled={disabled}
              fullWidth
              inputRef={ref}
              name={name}
              value={value}
              inputProps={{
                style: {
                  padding: '8px 16px',
                  ...(toUpperCase && { textTransform: 'uppercase' }),
                },
              }}
              placeholder={placeholder}
              {...restProps}
            />
          )}
      </div>
      <div className={classes.errorContainer} style={{ paddingLeft: labelWidth || 100 }}>
        <ErrorMessage className={classes.error} errors={errors || {}} name={name} as="p" />
      </div>
    </div>
  );
};

export const GenderField = ({
  labelWidth,
  ref,
  errors,
  ...restProps
}) => {
  const classes = useLabelFieldStyle();

  return (
    <div className={classes.container}>
      <div className={classes.fieldContainer}>
        <div className={classes.label} style={{ width: labelWidth || 100 }}>
          稱謂
          <span style={{ color: 'red' }}>*</span>
        </div>
        <RadioGroup ref={ref} {...restProps} row>
          <FormControlLabel
            control={<StyledRadio size="small" />}
            label={GENDER_STRING_MAP[GENDER.MALE]}
            value={GENDER.MALE}
          />
          <FormControlLabel
            control={<StyledRadio size="small" />}
            label={GENDER_STRING_MAP[GENDER.FEMALE]}
            value={GENDER.FEMALE}
          />
        </RadioGroup>
      </div>
      <div
        className={classes.errorContainer}
        style={{ paddingLeft: labelWidth || 100 }}
      >
        <ErrorMessage className={classes.error} errors={errors || {}} name="gender" as="p" />
      </div>
    </div>
  );
};

export const EmailField = ({
  labelWidth,
  control,
  errors,
}) => (
  <Controller
    control={control}
    name="email"
    render={({ ref, ...props }) => (
      <LabelTextField
        errors={errors}
        isRequired
        label="明細寄送信箱"
        labelWidth={labelWidth}
        placeholder="請填寫電子郵件"
        ref={ref}
        {...props}
      />
    )}
  />
);

export const BirthdayField = ({
  labelWidth,
  control,
  errors,
}) => {
  const classes = useLabelFieldStyle();

  return (
    <div className={classes.container}>
      <div className={classes.fieldContainer}>
        <div className={classes.label} style={{ width: labelWidth || 100 }}>
          生日
          <span style={{ color: 'red' }}>*</span>
        </div>
        <Controller
          control={control}
          name="year"
          render={({ ref, ...props }) => (
            <InputBase
              className={classes.input}
              inputRef={ref}
              placeholder="YYYY"
              inputMode="numeric"
              inputProps={{
                style: {
                  padding: '8px',
                },
              }}
              {...props}
            />
          )}
        />
        <Box mx={1}>/</Box>
        <Controller
          control={control}
          name="month"
          render={({ ref, ...props }) => (
            <InputBase
              className={classes.input}
              inputRef={ref}
              name="month"
              placeholder="MM"
              inputMode="numeric"
              inputProps={{
                style: {
                  padding: '8px',
                },
              }}
              {...props}
            />
          )}
        />
        <Box mx={1}>/</Box>
        <Controller
          control={control}
          name="day"
          render={({ ref, ...props }) => (
            <InputBase
              className={classes.input}
              inputRef={ref}
              name="day"
              placeholder="DD"
              inputMode="numeric"
              inputProps={{
                style: {
                  padding: '8px',
                },
              }}
              {...props}
            />
          )}
        />
      </div>
      <div
        className={classes.errorContainer}
        style={{ paddingLeft: labelWidth || 100 }}
      >
        <ErrorMessage className={classes.error} errors={errors || {}} name="year" as="p" />
        <ErrorMessage className={classes.error} errors={errors || {}} name="month" as="p" />
        <ErrorMessage className={classes.error} errors={errors || {}} name="day" as="p" />
      </div>
    </div>
  );
};

const schema = Joi.object({
  displayName: Joi.string().allow('').optional(),
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .messages({ 'string.empty': '明細寄送信箱為必填' }),
  mobileBarcode: Joi
    .string()
    .optional()
    .allow('')
    .pattern(/^\/[a-zA-Z0-9-+.]{7}$/)
    .messages({ 'string.pattern.base': '載具編號格式有誤' }),
  agreeTerms: Joi
    .boolean()
    .valid(true)
    .messages({ 'any.only': '必需同意' }),
  gender: Joi
    .string()
    .valid(GENDER.MALE, GENDER.FEMALE)
    .messages({
      'string.empty': '稱謂為必填',
      'any.only': '稱謂必須為先生或是小姐',
    })
    .required(),
  year: Joi.number().integer().min(1900).max(9999)
    .messages({ 'number.base': '年份為必填' })
    .required(),
  month: Joi.number().integer().min(1).max(12)
    .messages({ 'number.base': '月份為必填' })
    .required(),
  day: Joi.number().integer().min(1).max(31)
    .messages({ 'number.base': '日期為必填' })
    .required(),
}).and('year', 'month', 'day');

const useStyles = makeStyles(() => ({
  cancelRegisterButton: {
    flexShrink: 0,
    minWidth: 0,
    padding: 0,
    textDecoration: 'underline',
    color: 'grey',
  },
}));

export default ({
  data,
  reload,
  onSaveButtionClick,
  isLoading,
  hasUserVerifiedEnterpriseCard,
  verifyingEnterpriseCardExpireTime,
}) => {
  const classes = useStyles();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [
    isEnterpriseCardPayVerifyModalOpen,
    setIsEnterpriseCardPayVerifyModalOpen,
  ] = useState(null);
  const [
    isEnterpriseCardPayVerifyModalClear,
    setIsEnterpriseCardPayVerifyModalClear,
  ] = useState(null);
  const {
    displayName = '',
    mobileBarcode = '',
    gender = '',
    email = '',
    birthday,
  } = data || {};

  const [year = '', month = '', day = ''] = birthday || [];
  const {
    control,
    errors,
    getValues,
    trigger,
    reset,
  } = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    defaultValues: {
      displayName,
      email,
      gender,
      mobileBarcode,
      year,
      month,
      day,
      agreeTerms: true,
    },
    resolver: joiResolver(schema, {
      abortEarly: false,
      messages: {
        ...formErrorMessage,
        'number.max': '年份、月份或是日期格式有誤',
        'number.min': '年份、月份或是日期格式有誤',
      },
    }),
  });

  return (
    <>
      <ProgressBackdrop isShow={isLoading} />
      <form autoComplete="off">
        <Box
          display="flex"
          alignItems="stretch"
          flexWrap={{ sm: 'wrap' }}
          justifyContent={{ sm: 'space-between' }}
          pt={{ sm: 5 }}
          flexDirection={{
            xs: 'column',
            sm: 'row',
          }}
        >
          <ProfileBanner />
          <Section title="基本資料">
            <Controller
              control={control}
              name="displayName"
              render={({ ref, ...props }) => (
                <LabelTextField
                  readOnly
                  errors={errors}
                  label="暱稱"
                  labelWidth={60}
                  placeholder="請填寫暱稱"
                  ref={ref}
                  {...props}
                />
              )}
            />
            <Controller
              control={control}
              name="gender"
              render={({ ref, ...props }) => (
                <GenderField
                  labelWidth={60}
                  ref={ref}
                  errors={errors}
                  {...props}
                />
              )}
            />
            <BirthdayField
              control={control}
              labelWidth={60}
              errors={errors}
            />
          </Section>
          <Section title="發票資訊">
            <EmailField
              control={control}
              errors={errors}
              labelWidth={100}
            />
            <Controller
              control={control}
              name="mobileBarcode"
              render={({ ref, ...props }) => (
                <LabelTextField
                  errors={errors}
                  label="通用性載具"
                  labelWidth={100}
                  placeholder="請填寫載具編號"
                  ref={ref}
                  toUpperCase
                  {...props}
                />
              )}
            />
            <Controller
              control={control}
              name="agreeTerms"
              render={(params) => (
                <FormControlLabel
                  control={(
                    <Checkbox
                      {...params}
                      checked={params.value}
                      onChange={(event) => params.onChange(event.target.checked)}
                    />
                  )}
                  label={`我同意辦理退款時，由${config.BrandNameTW}代為處理電子發票折讓事宜，以加速退款作業`}
                />
              )}
            />
            <Box color="error.dark" fontSize="body2.fontSize">
              <ErrorMessage errors={errors} name="agreeTerms" />
            </Box>
          </Section>
          {
            (config.enableEnterpriseCardPay) ? (
              <Section title="使用企業員工卡支付">
                {
                  (!hasUserVerifiedEnterpriseCard && !verifyingEnterpriseCardExpireTime)
                    && (
                    <span>
                      您沒有已綁定的員工卡。
                      <StyledButton onClick={() => {
                        setIsEnterpriseCardPayVerifyModalOpen(true);
                        setIsEnterpriseCardPayVerifyModalClear(false);
                      }}
                      >
                        綁定員工卡
                      </StyledButton>
                    </span>
                    )
                }
                {
                  !!hasUserVerifiedEnterpriseCard && (
                  <span>
                    {'您已綁定企業員工卡。 '}
                    <Button
                      className={classes.cancelRegisterButton}
                      onClick={() => {
                        setIsEnterpriseCardPayVerifyModalOpen(true);
                        setIsEnterpriseCardPayVerifyModalClear(true);
                      }}
                    >
                      解除綁定
                    </Button>
                  </span>
                  )
                }
                {
                  !!verifyingEnterpriseCardExpireTime && (
                  <span>
                    <div>
                      請在
                      <span style={{ color: 'rgb(45,152,37)', margin: '0px 5px' }}>{moment(verifyingEnterpriseCardExpireTime).format('YYYY-MM-DD HH:mm')}</span>
                      前至
                      {config.BrandNameTW}
                      機台前感應員工卡開門一次完成綁定。
                    </div>
                    <Button
                      className={classes.cancelRegisterButton}
                      onClick={() => {
                        setIsEnterpriseCardPayVerifyModalOpen(true);
                        setIsEnterpriseCardPayVerifyModalClear(true);
                      }}
                    >
                      重新綁定
                    </Button>
                  </span>
                  )
                }
              </Section>
            ) : null
          }
          <Box mt={2.5} px={{ xs: 2.5, sm: 0 }} flexBasis={{ sm: '100%' }}>
            <StyledButton
              disableRipple
              disableFocusRipple
              fullWidth
              onClick={async () => {
                const isValid = await trigger();

                if (!isValid) return;

                const saveResult = await onSaveButtionClick(getValues());

                if (saveResult) {
                  reset(getValues());
                  setIsSnackbarOpen(true);
                }
              }}
            >
              資料更新
            </StyledButton>
          </Box>
        </Box>
      </form>
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={1500}
        onClose={() => {
          setIsSnackbarOpen(false);
        }}
      >
        <Alert
          variant="filled"
          onClose={() => {
            setIsSnackbarOpen(false);
          }}
          severity="success"
        >
          資料更新成功
        </Alert>
      </Snackbar>
      <EnterpriseCardPayVerifyModal
        isOpen={isEnterpriseCardPayVerifyModalOpen}
        onClose={() => setIsEnterpriseCardPayVerifyModalOpen(false)}
        reload={reload}
        isEnterpriseCardPayVerifyModalClear={isEnterpriseCardPayVerifyModalClear}
        verifyingEnterpriseCardExpireTime={verifyingEnterpriseCardExpireTime}
      />
    </>
  );
};
